import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import { useMsal } from '@azure/msal-react';

import { SecurityApi } from '../../../../services/SecurityApi';
import { PermissionApi } from '../../../../services/PermissionApi';
import { UserApi } from '../../../../services/UserApi';
import { loginRequest } from '../../../../utils/msal/authConfig';

// Components
import { LoginForm } from '.'
import { ChangePassword } from '../../../atoms/modalCards/ChangePassword'
import { Alert, Snackbar } from "@mui/material";

// Utils
import { Auth } from '../../../../utils/auth';
import Router from '../../../../router';
import { useTranslation } from 'react-i18next'

import { BoxModal, ModalContainer, ModalHeader, ModalBody, ModalFooter } from './styles';
import defineAbility from '../../../../config/defineAbility';
import { FormButton } from '../../../../pages/styles';
import { BackdropLoading } from '../../../BackdropLoading';

export const LoginFormContainer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { authenticate, saveProfile } = new Auth();
  const securityApi = new SecurityApi();
  const permissionApi = new PermissionApi();

  const { instance } = useMsal();
  const recaptchaRef = React.useRef();

  const [form, setForm] = React.useState({ username: '', roleId: null, password: '', reCaptchaToken: null })
  const [openChangePasswordForm, setOpenChangePasswordForm] = React.useState(false)
  const [messageError, setMessageError] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [open, setOpen] = React.useState(false);
  const [openRoleModal, setOpenRoleModal] = React.useState(false);
  const [selectedRoleId, setSelectedRoleId] = React.useState('');
  const [availableRoles, setAvailableRoles] = React.useState([]);
  const [roleModalError, setRoleModalError] = React.useState('');

  const handleChange = (event, name) => {
    const value = event.target.value;
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const resetCaptcha = () => {
    recaptchaRef.current?.reset();
    setForm(prev => ({ ...prev, reCaptchaToken: null }))
  }

  async function callPermissionsRole(id) {
    const response = await permissionApi.list(id);
    if (response?.status === 200) {
      let resp = response.data.data
      let permissions = []
      permissions = resp?.map((el) => { return { action: el.name } })
      permissions.push({ action: "All" })
      localStorage.setItem("permissions", JSON.stringify(permissions))
      defineAbility.update(permissions)
    }
  }

  const handleSubmit = async () => {
    setMessageError('');
    setIsLoading(true);

    // Primer login sin roleId
    const { roleId: _ignored, ...firstLoginPayload } = form;
    const resp = await securityApi.login(firstLoginPayload);
    setIsLoading(false);

    if (resp?.status === 200) {
      const token = resp.data.data.accessToken;
      const profile = resp.data.data.profile;
      const strings = resp.data.data.strings;
      authenticate(token);
      saveProfile(profile, strings);
      localStorage.setItem('loginInProgress', '1');
      await handleRolesOfUser();
    } else if (resp?.response?.status === 400) {
      setMessageError(resp?.response.data.description || resp?.response.data.data.description || '');
      if (resp?.response.data.code === 'expired' || resp?.response.data.data.code === 'expired') {
        setMessageError(t('password.validations.updatePassword'));
        setForm(prev => ({ ...prev, password: '' }));
        setOpenChangePasswordForm(true);
      }
      resetCaptcha();
    } else {
      setMessageError(t('password.validations.invalidCredentials'));
      resetCaptcha();
    }
  }

  const handleRolesOfUser = async () => {
    const userApi = new UserApi();
    const resp = await userApi.rolesOfUser();

    if (resp?.status === 200) {
      const roles = resp.data;
      if (!roles?.length) {
        localStorage.removeItem('token');
        localStorage.removeItem('profile');
        setMessageError(t('login.userNotFound'));
        return;
      }
      if (roles.length === 1) {
        await handleSecondLogin(roles[0].id);
      } else {
        setAvailableRoles(roles);
        setOpenRoleModal(true);
      }
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('profile');
      setMessageError(t('login.genericLoginError'));
    }
  }

  const handleSecondLogin = async (roleId) => {
    setIsLoading(true);
    const profileApi = new SecurityApi();
    const resp = await profileApi.getProfile(roleId);
    setIsLoading(false);

    if (resp?.status === 200) {
      const token = resp.data.data.accessToken;
      const profile = resp.data.data.profile;
      const strings = resp.data.data.strings;
      authenticate(token);
      saveProfile(profile, strings);
      await callPermissionsRole(resp.data.data.profile.Role.Id);
      localStorage.removeItem('loginInProgress');
      if (location?.state?.redirect) {
        navigate(location?.state?.redirect, { state: location?.state?.data });
        return;
      }
      navigate(Router.appMyWork);
    } else if (resp?.response?.status === 400) {
      setOpenRoleModal(false);
      setMessageError(
        resp?.response.data.description ||
        resp?.response.data.data.description ||
        t('login.genericLoginError')
      );
      if (resp?.response.data.code === 'expired' || resp?.response.data.data.code === 'expired') {
        setMessageError(t('password.validations.updatePassword'));
        setForm(prev => ({ ...prev, password: '' }));
        setOpenChangePasswordForm(true);
      }
      localStorage.removeItem('loginInProgress');
      localStorage.removeItem('token');
      localStorage.removeItem('profile');
    } else {
      setOpenRoleModal(false);
      setMessageError(
        resp?.response?.description ||
        resp?.response?.data?.description ||
        t('login.genericLoginError')
      );
      localStorage.removeItem('loginInProgress');
      localStorage.removeItem('token');
      localStorage.removeItem('profile');
    }
  }

  const handleCancelModal = async () => {
    setOpenRoleModal(false);
    setSelectedRoleId('');
    setRoleModalError('');
    setAvailableRoles([]);
    setForm({ username: '', roleId: null, password: '', reCaptchaToken: null });
    setMessageError('');
    recaptchaRef.current?.reset();
    localStorage.removeItem('loginInProgress');
    const auth = new Auth();
    await auth.logout();
  }

  const handleContinueModal = async () => {
    if (!selectedRoleId) {
      setRoleModalError(t('password.validations.role'));
      return;
    }
    setRoleModalError('');
    setOpenRoleModal(false);
    await handleSecondLogin(selectedRoleId);
  }

  const handleMicrosoftLogin = () => {
    loginRequest.redirectStartPage = Router.appSigninAzure;
    localStorage.setItem('userLogin', form.username);
    instance.loginRedirect(loginRequest).catch((e) => {
      console.error(e);
    });
  };

  const handleCloseChangePassword = () => {
    setOpenChangePasswordForm(false)
    setMessageError('')
  }

  return (
    <>
      <LoginForm
        form={form}
        setForm={setForm}
        messageError={messageError}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        isLoading={isLoading}
        recaptchaRef={recaptchaRef}
        setMessageError={setMessageError}
        setIsLoading={setIsLoading}
        setOpen={setOpen}
        handleMicrosoftLogin={handleMicrosoftLogin}
      />

      {/* Modal de selección de rol */}
      <Modal
        open={openRoleModal}
        disableEscapeKeyDown 
        onClose={(_, reason) => { if (reason === 'backdropClick') return; }}
      >
        <BoxModal>
          <ModalContainer>
            <ModalHeader>
              {t('login.roleSelectionTitle')}
            </ModalHeader>
            <ModalBody>
              <Typography sx={{ mb: 2 }}>{t('login.roleSelectionSubtitle')}</Typography>
              <FormControl fullWidth size="small">
                <InputLabel>{t('login.role')}</InputLabel>
                <Select
                  value={selectedRoleId}
                  label={t('login.role')}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                >
                  {availableRoles.map(role => (
                    <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {roleModalError && (
                <Typography color="error" sx={{ mt: 1, fontSize: '0.875rem' }}>
                  {roleModalError}
                </Typography>
              )}
            </ModalBody>
            <ModalFooter>
              <FormButton variant="outlined" type="close" onClick={handleCancelModal} disabled={isLoading}>
                {t('login.cancel')}
              </FormButton>
              <FormButton variant="contained" onClick={handleContinueModal} disabled={isLoading}>
                {t('login.continue')}
              </FormButton>
            </ModalFooter>
          </ModalContainer>
        </BoxModal>
      </Modal>

      <Modal open={openChangePasswordForm}>
        <BoxModal>
          <ChangePassword
            username={form.username}
            handleClose={handleCloseChangePassword}
          />
        </BoxModal>
      </Modal>

      <Snackbar
        open={open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setOpen(false)}
      >
        <Alert severity="error" color='error' onClose={() => setOpen(false)}>
          {t('password.validations.invalidAccount')}
        </Alert>
      </Snackbar>

      <BackdropLoading open={isLoading} />
    </>
  )
}
